type PermissionState = 'granted' | 'denied';

export const requestGyroscopePermission = async (): Promise<PermissionState> => {
  try {
    // Check if DeviceOrientationEvent is available and requires permission (iOS 13+)
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      return await (DeviceOrientationEvent as any).requestPermission();
    }
    // For non-iOS devices, permission is implicitly granted
    return 'granted';
  } catch (error) {
    console.error('Gyroscope permission error:', error);
    return 'denied';
  }
};

const gyroscopeSupported = typeof DeviceOrientationEvent !== 'undefined';

export type DeviceOrientationData = {
  absolute: boolean;
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
};

// Get gyroscope data at the moment of capture
export const getDeviceOrientation = () => new Promise<DeviceOrientationData | null>((resolve) => {
  if (!gyroscopeSupported) {
    resolve(null);
    return;
  }

  requestGyroscopePermission()
    .then((permission) => {
      if (permission !== 'granted') {
        resolve(null);
        return;
      }

      const handleOrientation = (event: DeviceOrientationEvent) => {
        window.removeEventListener('deviceorientation', handleOrientation);
        resolve({
          absolute: event.absolute,
          alpha: event.alpha,
          beta: event.beta,
          gamma: event.gamma
        });
      };

      window.addEventListener('deviceorientation', handleOrientation, { once: true });
      setTimeout(() => {
        window.removeEventListener('deviceorientation', handleOrientation);
        resolve(null);
      }, 1000);
    })
    .catch((error) => {
      console.error('Error requesting gyroscope permission:', error);
      resolve(null);
    });
});

/**
 * Starts listening to device orientation changes and calls the callback with updated values
 * @param onOrientation - Callback function that receives orientation updates
 * @param onError - Optional callback function for handling errors
 * @returns Cleanup function to stop listening to orientation changes
 */
export const watchDeviceOrientation = (
  onOrientation: (data: DeviceOrientationData) => void,
  onError?: (error: Error) => void
): (
) => void => {
  if (!gyroscopeSupported) {
    onError?.(new Error('Gyroscope not supported'));
    return () => {};
  }

  let isWatching = true;

  const handleOrientation = (event: DeviceOrientationEvent) => {
    if (!isWatching) return;

    onOrientation({
      absolute: event.absolute,
      alpha: event.alpha,
      beta: event.beta,
      gamma: event.gamma
    });
  };

  requestGyroscopePermission()
    .then((permission) => {
      if (permission !== 'granted') {
        onError?.(new Error('Gyroscope permission denied'));
        return;
      }

      window.addEventListener('deviceorientation', handleOrientation);
    })
    .catch((error) => {
      onError?.(error);
    });

  // Return cleanup function
  return () => {
    isWatching = false;
    window.removeEventListener('deviceorientation', handleOrientation);
  };
};
